/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React from 'react';
import { ToolContainerDraggable } from '@opengeoweb/shared';
import { COLUMN_DEFINITIONS } from '../../utils/profileDataPreferences';
import type {
  SoundingsColumnKey,
  SoundingsProfileDataPreferences,
  SoundingsProfileDataUnits,
} from '../../utils/profileDataPreferences';

export interface SoundingsProfileDataMenuProps {
  preferences: SoundingsProfileDataPreferences;
  onPreferencesChange: (next: SoundingsProfileDataPreferences) => void;
  isOpen?: boolean;
  handleClose: () => void;
  onMouseDown?: () => void;
  order?: number;
}

export const SoundingsProfileDataMenu = ({
  preferences,
  onPreferencesChange,
  isOpen = false,
  handleClose,
  onMouseDown = (): void => {},
  order = 0,
}: SoundingsProfileDataMenuProps): React.ReactElement => {
  const toggleColumnVisible = (key: SoundingsColumnKey): void => {
    onPreferencesChange({
      ...preferences,
      visibleColumns: {
        ...preferences.visibleColumns,
        [key]: !preferences.visibleColumns[key],
      },
    });
  };

  const setUnit = (
    unitKey: keyof SoundingsProfileDataUnits,
    value: string,
  ): void => {
    onPreferencesChange({
      ...preferences,
      units: {
        ...preferences.units,
        [unitKey]: value as SoundingsProfileDataUnits[typeof unitKey],
      },
    });
  };

  return (
    <ToolContainerDraggable
      bounds="parent"
      className="profile-data-menu"
      isOpen={isOpen}
      minWidth={320}
      onClose={handleClose}
      onMouseDown={onMouseDown}
      order={order}
      startPosition={{ top: 40, left: 50 }}
      startSize={{ width: 360 }}
      title="Profile Data"
    >
      <Stack spacing={1} sx={{ padding: 1.5 }}>
        {COLUMN_DEFINITIONS.map((definition) => {
          const {
            key,
            longLabel,
            shortLabel,
            unitKey,
            unitOptions,
            unitOptionLabels,
          } = definition;
          const visible = preferences.visibleColumns[key];
          const currentUnit = unitKey ? preferences.units[unitKey] : null;
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', minWidth: 130 }}
              >
                <Switch
                  checked={visible}
                  onChange={() => toggleColumnVisible(key)}
                  size="small"
                  slotProps={{
                    input: {
                      'aria-label': `Toggle ${longLabel} column`,
                    },
                  }}
                />
                <Typography variant="body2">
                  {longLabel}
                  <Box
                    component="span"
                    sx={{ color: 'text.secondary', ml: 0.5 }}
                  >
                    ({shortLabel})
                  </Box>
                </Typography>
              </Box>
              {unitOptions && unitKey && currentUnit ? (
                <ToggleButtonGroup
                  aria-label={`${longLabel} unit`}
                  exclusive
                  onChange={(_, value: string | null) => {
                    if (value !== null) {
                      setUnit(unitKey, value);
                    }
                  }}
                  size="small"
                  value={currentUnit}
                >
                  {unitOptions.map((option) => (
                    <ToggleButton
                      key={option}
                      sx={{ px: 1, py: 0.25, textTransform: 'none' }}
                      value={option}
                    >
                      {unitOptionLabels?.[option] ?? option}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ) : (
                <Box sx={{ width: 1, maxWidth: 0 }} />
              )}
            </Box>
          );
        })}
      </Stack>
    </ToolContainerDraggable>
  );
};
